import type { FC } from 'react'
import { useState } from 'react'

import { InfoBox, MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { getPricePrecision } from '@skytech/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import AlertCircleIcon from 'mdi-react/AlertCircleIcon'

import { queryClient } from '@/app/queryClient'
import { Translation } from '@/app/translations/Translation'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { LocalDate } from '@/components/LocalDate'
import queries from '@/modules/admin/queries'
import type { AssetExtended } from '@/modules/admin/resources'
import {
  getActiveBuyoutOfferByAsset,
  withdrawBuyoutOffer,
} from '@/modules/admin/resources/buyout-offers'

type ActiveBuyoutOfferBannerProps = {
  assetDetails: AssetExtended
}

export const ActiveBuyoutOfferBanner: FC<ActiveBuyoutOfferBannerProps> = ({ assetDetails }) => {
  const [isWithdrawConfirmationOpen, setIsWithdrawConfirmationOpen] = useState(false)

  const { data: activeBuyoutOffer, isLoading } = useQuery(
    queries.adminActiveBuyoutOfferByAsset(assetDetails.id),
    () => getActiveBuyoutOfferByAsset(assetDetails.id),
    { enabled: Boolean(assetDetails.id) }
  )

  const withdrawOfferMutation = useMutation(
    () => {
      if (!activeBuyoutOffer) throw new Error('No active offer to withdraw')
      return withdrawBuyoutOffer(activeBuyoutOffer.id)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queries.adminActiveBuyoutOfferByAsset(assetDetails.id))
        queryClient.invalidateQueries(queries.adminAsset(assetDetails.id))
        setIsWithdrawConfirmationOpen(false)
      },
    }
  )

  if (isLoading || !activeBuyoutOffer) {
    return null
  }

  return (
    <>
      <InfoBox emphasis="warning">
        <div className="flex w-full flex-col gap-2">
          <div className="flex items-center gap-2">
            <AlertCircleIcon />
            <h3 className="m-0 font-bold">
              <Translation id="Active Buyout Offer" />
            </h3>
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <p className="m-0">
              <Translation id="Employee" />: <strong>{activeBuyoutOffer.costObject.name}</strong>
            </p>
            <p className="m-0">
              <Translation id="Price" />:{' '}
              <strong>
                <FormattedNumberWithCurrency
                  amount={activeBuyoutOffer.price}
                  precision={getPricePrecision(activeBuyoutOffer.price)}
                />
              </strong>
            </p>
            <p className="m-0">
              <Translation id="Expires" />:{' '}
              <strong>
                <LocalDate date={activeBuyoutOffer.offerExpirationDate} />
              </strong>
            </p>
          </div>

          <p className="m-0 text-sm">
            <Translation id="This asset is locked while there is an active buyout offer. Withdraw the offer to make changes." />
          </p>
        </div>
      </InfoBox>
      <div className="mb-4 mt-1 flex justify-end">
        <Button variant="danger" onClick={() => setIsWithdrawConfirmationOpen(true)}>
          <Translation id="Withdraw Offer" />
        </Button>
      </div>

      <MessagePopup
        isOpen={isWithdrawConfirmationOpen}
        onClose={() => setIsWithdrawConfirmationOpen(false)}
        title={<Translation id="Withdraw Buyout Offer" />}
        message={<Translation id="Are you sure you want to withdraw this buyout offer?" />}
        type="warning"
      >
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setIsWithdrawConfirmationOpen(false)}>
            <Translation id="Cancel" />
          </Button>
          <Button
            variant="danger"
            isLoading={withdrawOfferMutation.isLoading}
            onClick={() => withdrawOfferMutation.mutate()}
          >
            <Translation id="Withdraw" />
          </Button>
        </div>
      </MessagePopup>
    </>
  )
}
